import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Link2, Copy, Check, ExternalLink, Mail, Trash2, X, Plus, RefreshCw, ShoppingCart } from 'lucide-react';
import axios from 'axios';
import Modal from '../common/ui/Modal';
import { formatDateTime } from '../../utils/dateUtils';

const WP_ADMIN_LINKS_URL = 'https://b2b.naviter.com/wp-admin/edit.php?post_type=plugify_ccl';

// API conversation id = FIRST numeric segment of the Help Scout URL
const extractHsConversationId = (link) => {
  const match = /\/conversation\/(\d+)/.exec(link || '');
  return match ? match[1] : null;
};

// Discount options block, shared by the create form and the merge modal
const DiscountOptions = ({ apply, onApply, percent, onPercent, valid }) => (
  <div className="space-y-1.5 pt-1">
    <label className="flex items-center text-xs text-gray-700 cursor-pointer">
      <input
        type="checkbox"
        checked={apply}
        onChange={(e) => onApply(e.target.checked)}
        className="h-3.5 w-3.5 mr-1.5 rounded border-gray-300 text-naviter-blue focus:ring-naviter-blue"
      />
      Apply discount
    </label>
    {apply && (
      <div className="pl-5 space-y-1.5">
        <div className="flex items-center space-x-2 text-xs text-gray-700">
          <span>Percentage:</span>
          <input
            type="number"
            min="1"
            max="100"
            step="1"
            value={percent}
            onChange={(e) => onPercent(e.target.value)}
            className="w-16 px-1.5 py-0.5 text-xs border border-gray-300 rounded text-center"
          />
          <span>%</span>
        </div>
        {!valid && (
          <div className="text-xs text-red-600">Enter a percentage between 1 and 100</div>
        )}
      </div>
    )}
  </div>
);

// Payment links card on the repair view sidebar: attach WooCommerce cart links
// (picked from the shop or created here), copy them, and email them to the
// customer. Server routes live in server/routes/repairs.js under
// /api/repairs/cart-links and /api/repairs/:id/payment-links.
const PaymentLinks = forwardRef(({ repairId, customerEmail, deviceModel, serialNumber, helpScoutLink, onRefresh }, ref) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Kept separate from `error` so a failed action doesn't replace the list
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  // Add section: null (collapsed) | 'existing' | 'create'
  const [addMode, setAddMode] = useState(null);
  const [attaching, setAttaching] = useState(false);

  // "Select existing" state
  const [shopLinks, setShopLinks] = useState([]);
  const [shopLoading, setShopLoading] = useState(false);
  const [shopError, setShopError] = useState('');
  const [selectedShopLinkId, setSelectedShopLinkId] = useState('');
  const [shopFilter, setShopFilter] = useState('');

  // "Create new" state
  const [newTitle, setNewTitle] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productSearching, setProductSearching] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [discountPercent, setDiscountPercent] = useState('');

  // Per-row state
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [deletingLinkId, setDeletingLinkId] = useState(null);

  // Send modal state (creates a Help Scout DRAFT, nothing is emailed directly)
  const [sendModalLink, setSendModalLink] = useState(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  // Saved (pending) products of this repair, awaiting cart creation
  const [pendingItems, setPendingItems] = useState([]);
  const [savingForLater, setSavingForLater] = useState(false);
  const [quickCreating, setQuickCreating] = useState(false);

  // "Merge to one cart" modal state
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeOverview, setMergeOverview] = useState([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const [selectedMergeIds, setSelectedMergeIds] = useState([]);
  const [mergeTitle, setMergeTitle] = useState('');
  const [mergeTitleEdited, setMergeTitleEdited] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeApplyDiscount, setMergeApplyDiscount] = useState(false);
  const [mergeDiscountPercent, setMergeDiscountPercent] = useState('');

  const hsConversationId = extractHsConversationId(helpScoutLink);

  const fetchLinks = useCallback(async () => {
    if (!repairId) {
      setLinks([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`/api/repairs/${repairId}/payment-links`);
      setLinks(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      console.error('Error fetching payment links:', err);
      setError('Failed to load payment links');
    } finally {
      setLoading(false);
    }
  }, [repairId]);

  const fetchPendingItems = useCallback(async () => {
    if (!repairId) {
      setPendingItems([]);
      return;
    }
    try {
      const response = await axios.get(`/api/repairs/${repairId}/cart-items`);
      setPendingItems(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching saved products:', err);
    }
  }, [repairId]);

  useEffect(() => {
    fetchLinks();
    fetchPendingItems();
  }, [fetchLinks, fetchPendingItems]);

  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchLinks();
      fetchPendingItems();
    }
  }), [fetchLinks, fetchPendingItems]);

  const fetchShopLinks = useCallback(async () => {
    try {
      setShopLoading(true);
      setShopError('');
      const response = await axios.get('/api/repairs/cart-links');
      setShopLinks(Array.isArray(response.data?.links) ? response.data.links : []);
    } catch (err) {
      console.error('Error fetching shop cart links:', err);
      setShopError(err.response?.data?.error || 'Failed to load links from the shop');
    } finally {
      setShopLoading(false);
    }
  }, []);

  // Load the shop links when the "existing" picker opens
  useEffect(() => {
    if (addMode === 'existing' && shopLinks.length === 0 && !shopLoading && !shopError) {
      fetchShopLinks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addMode]);

  // Debounced product search for the create form
  useEffect(() => {
    if (addMode !== 'create' || productSearch.trim().length < 2) {
      setProductResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setProductSearching(true);
        const response = await axios.get('/api/repairs/cart-link-products', {
          params: { search: productSearch.trim() }
        });
        setProductResults(Array.isArray(response.data?.products) ? response.data.products : []);
      } catch (err) {
        console.error('Error searching shop products:', err);
        setProductResults([]);
      } finally {
        setProductSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [productSearch, addMode]);

  const openAddSection = (mode) => {
    setAddMode(addMode === mode ? null : mode);
    setActionError('');
    if (mode === 'create' && !newTitle) {
      const parts = [`Repair #${repairId}`];
      if (deviceModel) parts.push(deviceModel);
      if (serialNumber) parts.push(`SN ${serialNumber}`);
      setNewTitle(parts.join(' - '));
    }
  };

  const handleAttachExisting = async () => {
    const shopLink = shopLinks.find((l) => String(l.id) === String(selectedShopLinkId));
    if (!shopLink) return;
    try {
      setAttaching(true);
      setActionError('');
      await axios.post(`/api/repairs/${repairId}/payment-links`, {
        url: shopLink.cart_url,
        label: shopLink.title,
        wp_post_id: shopLink.id
      });
      setSelectedShopLinkId('');
      setAddMode(null);
      setActionSuccess('Payment link attached');
      await fetchLinks();
      onRefresh?.();
    } catch (err) {
      console.error('Error attaching payment link:', err);
      setActionError(err.response?.data?.error || 'Failed to attach payment link');
    } finally {
      setAttaching(false);
    }
  };

  const discountValid = !applyDiscount
    || (Number(discountPercent) > 0 && Number(discountPercent) <= 100);

  const handleCreateAndAttach = async () => {
    if (!newTitle.trim() || selectedProducts.length === 0 || !discountValid) return;
    try {
      setAttaching(true);
      setActionError('');
      const createNew = {
        title: newTitle.trim(),
        products: selectedProducts.map((p) => ({ product_id: p.id, quantity: p.quantity }))
      };
      if (applyDiscount) {
        createNew.discount = {
          type: 'percentage',
          amount: Number(discountPercent)
        };
      }
      await axios.post(`/api/repairs/${repairId}/payment-links`, { create_new: createNew });
      setNewTitle('');
      setProductSearch('');
      setSelectedProducts([]);
      setApplyDiscount(false);
      setDiscountPercent('');
      setAddMode(null);
      setActionSuccess('Cart link created in the shop and attached');
      await fetchLinks();
      onRefresh?.();
    } catch (err) {
      console.error('Error creating payment link:', err);
      setActionError(err.response?.data?.error || 'Failed to create the cart link');
    } finally {
      setAttaching(false);
    }
  };

  // Save the selected products on the repair for a later (possibly combined) cart
  const handleSaveForLater = async () => {
    if (selectedProducts.length === 0) return;
    try {
      setSavingForLater(true);
      setActionError('');
      await axios.post(`/api/repairs/${repairId}/cart-items`, {
        products: selectedProducts.map((p) => ({ product_id: p.id, product_name: p.name, quantity: p.quantity }))
      });
      setProductSearch('');
      setSelectedProducts([]);
      setAddMode(null);
      setActionSuccess('Products saved for later cart creation');
      await fetchPendingItems();
      onRefresh?.();
    } catch (err) {
      console.error('Error saving products:', err);
      setActionError(err.response?.data?.error || 'Failed to save products');
    } finally {
      setSavingForLater(false);
    }
  };

  const handlePendingQtyChange = async (item, qty) => {
    setPendingItems(pendingItems.map((it) => (it.id === item.id ? { ...it, quantity: qty } : it)));
    try {
      await axios.post(`/api/repairs/${repairId}/cart-items`, {
        products: [{ product_id: item.product_id, product_name: item.product_name, quantity: qty }]
      });
    } catch (err) {
      console.error('Error updating saved product quantity:', err);
      setActionError(err.response?.data?.error || 'Failed to update quantity');
      await fetchPendingItems();
    }
  };

  const handlePendingDelete = async (item) => {
    try {
      setActionError('');
      await axios.delete(`/api/repairs/${repairId}/cart-items/${item.id}`);
      await fetchPendingItems();
    } catch (err) {
      console.error('Error removing saved product:', err);
      setActionError(err.response?.data?.error || 'Failed to remove saved product');
    }
  };

  // One-click path for a single repair: build the cart from THIS repair's
  // saved products, attach it and go straight to the send step. For combining
  // several repairs (or adding a discount) the merge modal is the way.
  const handleCreateFromSaved = async () => {
    if (pendingItems.length === 0) return;
    try {
      setQuickCreating(true);
      setActionError('');
      const parts = [`Repair #${repairId}`];
      if (deviceModel) parts.push(deviceModel);
      if (serialNumber) parts.push(`SN ${serialNumber}`);
      const response = await axios.post(`/api/repairs/${repairId}/payment-links`, {
        merge: { repair_ids: [Number(repairId)], title: parts.join(' - ') }
      });
      setActionSuccess('Cart created from the saved products and attached');
      await fetchLinks();
      await fetchPendingItems();
      onRefresh?.();
      // straight on to sending - the modal confirms the Help Scout draft
      if (response.data?.id) {
        openSendModal(response.data);
      }
    } catch (err) {
      console.error('Error creating cart from saved products:', err);
      setActionError(err.response?.data?.error || 'Failed to create the cart');
    } finally {
      setQuickCreating(false);
    }
  };

  // Only the repair numbers - the customer name on a repair is written too
  // inconsistently to belong in the cart title.
  const defaultMergeTitle = (ids) => {
    const sorted = [...ids].sort((a, b) => a - b);
    return `${sorted.length === 1 ? 'Repair' : 'Repairs'} #${sorted.join(', #')}`;
  };

  const openMergeModal = async () => {
    setMergeOpen(true);
    setMergeError('');
    setMergeTitleEdited(false);
    setMergeApplyDiscount(false);
    setMergeDiscountPercent('');
    try {
      setMergeLoading(true);
      const response = await axios.get('/api/repairs/cart-items-overview');
      const overview = Array.isArray(response.data) ? response.data : [];
      setMergeOverview(overview);
      const initial = overview.some((r) => Number(r.id) === Number(repairId)) ? [Number(repairId)] : [];
      setSelectedMergeIds(initial);
      setMergeTitle(defaultMergeTitle(initial));
    } catch (err) {
      console.error('Error fetching repairs with saved products:', err);
      setMergeError(err.response?.data?.error || 'Failed to load repairs with saved products');
    } finally {
      setMergeLoading(false);
    }
  };

  const toggleMergeRepair = (rid) => {
    const id = Number(rid);
    const next = selectedMergeIds.includes(id)
      ? selectedMergeIds.filter((x) => x !== id)
      : [...selectedMergeIds, id];
    setSelectedMergeIds(next);
    if (!mergeTitleEdited) {
      setMergeTitle(defaultMergeTitle(next));
    }
  };

  const mergeDiscountValid = !mergeApplyDiscount
    || (Number(mergeDiscountPercent) > 0 && Number(mergeDiscountPercent) <= 100);

  // Selecting repairs of different customers is allowed but almost surely a
  // mistake. Same email = same customer; a repair without an email counts as
  // its own customer.
  const mergeCustomerMismatch = new Set(
    mergeOverview
      .filter((r) => selectedMergeIds.includes(Number(r.id)))
      .map((r) => (r.customer_email || '').trim().toLowerCase() || `no-email-${r.id}`)
  ).size > 1;

  // Repairs grouped by customer email for the merge modal; the group holding
  // the current repair comes first. Repairs without an email each form their
  // own group (an empty email is no proof of the same customer).
  const mergeGroups = (() => {
    const map = new Map();
    for (const r of mergeOverview) {
      const email = (r.customer_email || '').trim().toLowerCase();
      const key = email || `no-email-${r.id}`;
      if (!map.has(key)) map.set(key, { key, email, rows: [] });
      map.get(key).rows.push(r);
    }
    const groups = [...map.values()];
    groups.sort((a, b) => {
      const aHas = a.rows.some((r) => Number(r.id) === Number(repairId));
      const bHas = b.rows.some((r) => Number(r.id) === Number(repairId));
      if (aHas !== bHas) return aHas ? -1 : 1;
      return 0;
    });
    return groups;
  })();

  // Group checkbox: all selected -> deselect the group, otherwise select it all
  const toggleMergeGroup = (group) => {
    const groupIds = group.rows.map((r) => Number(r.id));
    const allSelected = groupIds.every((gid) => selectedMergeIds.includes(gid));
    const next = allSelected
      ? selectedMergeIds.filter((x) => !groupIds.includes(x))
      : [...new Set([...selectedMergeIds, ...groupIds])];
    setSelectedMergeIds(next);
    if (!mergeTitleEdited) {
      setMergeTitle(defaultMergeTitle(next));
    }
  };

  const handleMerge = async () => {
    if (selectedMergeIds.length === 0 || !mergeTitle.trim() || !mergeDiscountValid) return;
    try {
      setMerging(true);
      setMergeError('');
      const merge = { repair_ids: selectedMergeIds, title: mergeTitle.trim() };
      if (mergeApplyDiscount) {
        merge.discount = {
          type: 'percentage',
          amount: Number(mergeDiscountPercent)
        };
      }
      await axios.post(`/api/repairs/${repairId}/payment-links`, { merge });
      setMergeOpen(false);
      setActionSuccess(`Combined cart created and attached to ${selectedMergeIds.length} repair${selectedMergeIds.length === 1 ? '' : 's'}`);
      await fetchLinks();
      await fetchPendingItems();
      onRefresh?.();
    } catch (err) {
      console.error('Error creating combined cart:', err);
      setMergeError(err.response?.data?.error || 'Failed to create the combined cart');
    } finally {
      setMerging(false);
    }
  };

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiedLinkId(link.id);
      setTimeout(() => setCopiedLinkId(null), 1500);
    } catch (err) {
      console.error('Error copying link:', err);
      setActionError('Failed to copy the link');
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm('Remove this payment link from the repair? (The link itself stays in the shop.)')) {
      return;
    }
    try {
      setDeletingLinkId(link.id);
      setActionError('');
      await axios.delete(`/api/repairs/${repairId}/payment-links/${link.id}`);
      await fetchLinks();
      onRefresh?.();
    } catch (err) {
      console.error('Error removing payment link:', err);
      setActionError(err.response?.data?.error || 'Failed to remove payment link');
    } finally {
      setDeletingLinkId(null);
    }
  };

  const openSendModal = (link) => {
    setSendModalLink(link);
    setSendEmail(customerEmail || '');
    setSendError('');
  };

  // Without an existing conversation a customer email is needed to start one
  const sendReady = hsConversationId ? true : sendEmail.includes('@');

  const handleSend = async () => {
    if (!sendModalLink || !sendReady) return;
    try {
      setSending(true);
      setSendError('');
      const payload = hsConversationId ? {} : { email: sendEmail.trim() };
      const response = await axios.post(
        `/api/repairs/${repairId}/payment-links/${sendModalLink.id}/send`,
        payload
      );
      setSendModalLink(null);
      setActionSuccess(response.data?.message || 'Draft prepared in Help Scout');
      // Open the conversation so the agent can review and send the draft
      if (response.data?.conversation_url) {
        window.open(response.data.conversation_url, '_blank', 'noopener');
      }
      await fetchLinks();
      onRefresh?.();
    } catch (err) {
      console.error('Error creating payment link draft:', err);
      setSendError(err.response?.data?.error || 'Failed to create the Help Scout draft');
    } finally {
      setSending(false);
    }
  };

  const addProduct = (product) => {
    if (!selectedProducts.some((p) => p.id === product.id)) {
      setSelectedProducts([...selectedProducts, { ...product, quantity: 1 }]);
    }
    setProductSearch('');
    setProductResults([]);
  };

  const filteredShopLinks = shopFilter.trim()
    ? shopLinks.filter((l) => (l.title || '').toLowerCase().includes(shopFilter.trim().toLowerCase()))
    : shopLinks;

  if (loading) {
    return (
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Links</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading payment links...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Links</h3>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div id="payment-links">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Payment Links</h3>
        <a
          href={WP_ADMIN_LINKS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-400 hover:text-naviter-blue flex items-center"
          title="Manage cart links in WordPress admin"
        >
          Manage in WP
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      </div>

      {/* Action feedback - inline so the list stays visible */}
      {actionError && (
        <div className="mb-3 flex items-start justify-between bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <span className="text-sm text-red-700">{actionError}</span>
          <button onClick={() => setActionError('')} className="ml-2 text-red-400 hover:text-red-600" title="Dismiss">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {actionSuccess && (
        <div className="mb-3 flex items-start justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2">
          <span className="text-sm text-green-700">{actionSuccess}</span>
          <button onClick={() => setActionSuccess('')} className="ml-2 text-green-400 hover:text-green-600" title="Dismiss">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Attached links */}
      {links.length === 0 ? (
        <div className="text-center py-6">
          <ShoppingCart className="mx-auto h-10 w-10 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">No payment links yet</p>
        </div>
      ) : (
        <div className="space-y-2 mb-4">
          {links.map((link) => {
            const isSent = !!link.sent_at;
            return (
              <div key={link.id} className="border border-gray-200 rounded-md px-3 py-2">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center text-sm text-gray-900">
                      <Link2 className="h-3.5 w-3.5 text-gray-400 mr-1.5 flex-shrink-0" />
                      <span className="font-medium truncate" title={link.label || link.url}>
                        {link.label || link.url}
                      </span>
                      {isSent && (
                        <span
                          className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 flex-shrink-0"
                          title={`Sent to ${link.sent_to} on ${formatDateTime(link.sent_at)}${link.sent_by_name ? ` by ${link.sent_by_name}` : ''}`}
                        >
                          Sent
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 truncate" title={link.url}>{link.url}</div>
                  </div>
                  <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                    <button
                      onClick={() => handleCopy(link)}
                      className={`p-1 transition-colors ${copiedLinkId === link.id ? 'text-green-600' : 'text-gray-400 hover:text-naviter-blue'}`}
                      title="Copy link"
                    >
                      {copiedLinkId === link.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-gray-400 hover:text-naviter-blue transition-colors"
                      title="Open link"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    <button
                      onClick={() => openSendModal(link)}
                      className={`p-1 transition-colors ${isSent ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-naviter-blue'}`}
                      title={isSent ? `Sent to ${link.sent_to} on ${formatDateTime(link.sent_at)} - click to prepare again` : 'Prepare Help Scout draft for the customer'}
                    >
                      <Mail className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(link)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Remove from repair"
                      disabled={deletingLinkId === link.id}
                    >
                      {deletingLinkId === link.id ? (
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-red-600 border-t-transparent"></div>
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Saved (pending) products awaiting cart creation */}
      {pendingItems.length > 0 && (
        <div className="mb-4 border border-dashed border-gray-300 rounded-md px-3 py-2">
          <div className="text-xs font-medium text-gray-700 mb-1.5">Saved products (waiting for cart)</div>
          <div className="space-y-1">
            {pendingItems.map((item) => (
              <div key={item.id} className="flex items-center justify-between text-xs bg-gray-50 rounded-md px-2 py-1">
                <span className="truncate" title={item.product_name}>{item.product_name}</span>
                <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={item.quantity}
                    onChange={(e) => {
                      const qty = Math.max(1, parseInt(e.target.value, 10) || 1);
                      handlePendingQtyChange(item, qty);
                    }}
                    className="w-14 px-1 py-0.5 text-xs border border-gray-300 rounded text-center"
                  />
                  <button
                    onClick={() => handlePendingDelete(item)}
                    className="p-0.5 text-gray-400 hover:text-red-600"
                    title="Remove saved product"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex space-x-2">
            <button
              onClick={handleCreateFromSaved}
              disabled={quickCreating}
              className="flex-1 px-2 py-1.5 text-xs font-medium rounded-md bg-naviter-blue text-white hover:bg-naviter-blue-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Create a cart from this repair's saved products, attach it and continue to sending"
            >
              {quickCreating ? 'Creating cart...' : 'Create cart & send...'}
            </button>
            <button
              onClick={openMergeModal}
              disabled={quickCreating}
              className="flex-1 px-2 py-1.5 text-xs font-medium rounded-md border border-naviter-blue text-naviter-blue hover:bg-naviter-blue hover:text-white disabled:opacity-50 transition-colors"
              title="Combine saved products of several repairs into one cart (discount options available there)"
            >
              Merge to one cart...
            </button>
          </div>
        </div>
      )}

      {/* Add section toggle buttons */}
      <div className="flex space-x-2">
        <button
          onClick={() => openAddSection('existing')}
          className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md border transition-colors ${
            addMode === 'existing'
              ? 'bg-naviter-blue text-white border-naviter-blue'
              : 'text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          Select existing link
        </button>
        <button
          onClick={() => openAddSection('create')}
          className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md border transition-colors ${
            addMode === 'create'
              ? 'bg-naviter-blue text-white border-naviter-blue'
              : 'text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          <span className="inline-flex items-center"><Plus className="h-3 w-3 mr-1" />Create new link</span>
        </button>
      </div>

      {/* Select existing link */}
      {addMode === 'existing' && (
        <div className="mt-3 space-y-2">
          {shopError ? (
            <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700 flex items-center justify-between">
              <span>{shopError}</span>
              <button onClick={fetchShopLinks} className="ml-2 text-red-400 hover:text-red-600" title="Retry">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : shopLoading ? (
            <div className="flex items-center text-sm text-gray-500 py-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Loading links from the shop...
            </div>
          ) : (
            <>
              <input
                type="text"
                value={shopFilter}
                onChange={(e) => setShopFilter(e.target.value)}
                placeholder="Filter links..."
                className="w-full px-3 py-1.5 text-sm border-2 border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="border-2 border-gray-200 rounded-md max-h-56 overflow-y-auto">
                {filteredShopLinks.length === 0 ? (
                  <div className="px-2 py-2 text-xs text-gray-400">No links match the filter</div>
                ) : (
                  filteredShopLinks.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => setSelectedShopLinkId(String(l.id))}
                      className={`w-full text-left px-2 py-1 text-xs truncate transition-colors ${
                        String(selectedShopLinkId) === String(l.id)
                          ? 'bg-naviter-blue text-white'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                      title={l.title}
                    >
                      {l.title}
                    </button>
                  ))
                )}
              </div>
              <button
                onClick={handleAttachExisting}
                disabled={!selectedShopLinkId || attaching}
                className="w-full px-3 py-1.5 text-sm font-medium text-white bg-naviter-blue hover:bg-naviter-blue-dark rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {attaching ? 'Attaching...' : 'Attach to repair'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Create new link */}
      {addMode === 'create' && (
        <div className="mt-3 space-y-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Link title"
            className="w-full px-3 py-1.5 text-sm border-2 border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          {/* Product search */}
          <div className="relative">
            <input
              type="text"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder="Search shop products (min 2 characters)..."
              className="w-full px-3 py-1.5 text-sm border-2 border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {productSearching && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            )}
            {productResults.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                {productResults.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => addProduct(p)}
                    className="w-full text-left px-2 py-1 text-xs hover:bg-gray-50 flex items-center justify-between"
                    title={p.name}
                  >
                    <span className="truncate">{p.name}</span>
                    {p.price && <span className="ml-2 text-xs text-gray-400 flex-shrink-0">{p.price} EUR</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected products with quantities */}
          {selectedProducts.length > 0 && (
            <div className="space-y-1">
              {selectedProducts.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-xs bg-gray-50 rounded-md px-2 py-1">
                  <span className="truncate" title={p.name}>{p.name}</span>
                  <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={p.quantity}
                      onChange={(e) => {
                        const qty = Math.max(1, parseInt(e.target.value, 10) || 1);
                        setSelectedProducts(selectedProducts.map((sp) => (sp.id === p.id ? { ...sp, quantity: qty } : sp)));
                      }}
                      className="w-14 px-1 py-0.5 text-sm border border-gray-300 rounded text-center"
                    />
                    <button
                      onClick={() => setSelectedProducts(selectedProducts.filter((sp) => sp.id !== p.id))}
                      className="p-0.5 text-gray-400 hover:text-red-600"
                      title="Remove product"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Discount options (percentage only; written into the plugify_ccl meta) */}
          <DiscountOptions
            apply={applyDiscount}
            onApply={setApplyDiscount}
            percent={discountPercent}
            onPercent={setDiscountPercent}
            valid={discountValid}
          />

          <button
            onClick={handleCreateAndAttach}
            disabled={!newTitle.trim() || selectedProducts.length === 0 || !discountValid || attaching}
            className="w-full px-3 py-1.5 text-sm font-medium text-white bg-naviter-blue hover:bg-naviter-blue-dark rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {attaching ? 'Creating...' : 'Create in shop & attach'}
          </button>
          <button
            onClick={handleSaveForLater}
            disabled={selectedProducts.length === 0 || savingForLater}
            className="w-full px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Save the selected products on this repair without creating a cart - combine them later via 'Merge to one cart'"
          >
            {savingForLater ? 'Saving...' : 'Save products for later (no cart yet)'}
          </button>
        </div>
      )}

      {/* Merge to one cart modal */}
      <Modal
        isOpen={mergeOpen}
        onClose={() => !merging && setMergeOpen(false)}
        title="Merge to one cart"
        size="lg"
      >
        <div className="space-y-3">
          <p className="text-xs text-gray-600">
            Select the repairs whose saved products should go into ONE cart. The created link is attached to
            all selected repairs and their saved products are cleared.
          </p>

          {mergeLoading ? (
            <div className="flex items-center text-sm text-gray-500 py-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Loading repairs with saved products...
            </div>
          ) : (
            <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto">
              {mergeOverview.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400">No repairs with saved products</div>
              ) : (
                mergeGroups.map((group) => {
                  const groupIds = group.rows.map((r) => Number(r.id));
                  const allSelected = groupIds.every((gid) => selectedMergeIds.includes(gid));
                  // The header identifies the CUSTOMER, so it shows only the
                  // shared email - repairs in the group may carry differently
                  // written names, and picking one of them would be misleading.
                  // A no-email group holds a single repair; there the name is
                  // the only identity available.
                  const headerLabel = group.email
                    || `${group.rows[0].customer_name || 'Unknown customer'} (no email)`;
                  return (
                    <div key={group.key} className="border-b border-gray-100 last:border-b-0">
                      {/* Customer group header - same email = same customer; the
                          checkbox toggles the whole group */}
                      <label className="flex items-center px-3 py-1.5 bg-gray-100 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => toggleMergeGroup(group)}
                          className="h-3.5 w-3.5 mr-2 rounded border-gray-300 text-naviter-blue focus:ring-naviter-blue flex-shrink-0"
                        />
                        <span className="text-xs font-semibold text-gray-700 truncate" title={headerLabel}>
                          {headerLabel}
                        </span>
                      </label>
                      {group.rows.map((r) => {
                        const rid = Number(r.id);
                        const checked = selectedMergeIds.includes(rid);
                        const items = Array.isArray(r.items) ? r.items : [];
                        return (
                          <label key={rid} className="flex items-start pl-6 pr-3 py-2 cursor-pointer hover:bg-gray-50">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleMergeRepair(rid)}
                              className="h-3.5 w-3.5 mt-0.5 mr-2 rounded border-gray-300 text-naviter-blue focus:ring-naviter-blue flex-shrink-0"
                            />
                            <span className="min-w-0">
                              <span className="block text-xs font-medium text-gray-900 truncate">
                                #{rid}{rid === Number(repairId) ? ' (this repair)' : ''}
                                {r.device_model ? ` - ${r.device_model}` : ''}{r.device_serial ? ` SN ${r.device_serial}` : ''}
                              </span>
                              <span className="block text-xs text-gray-400 truncate" title={items.map((it) => `${it.quantity}x ${it.product_name}`).join(', ')}>
                                {items.map((it) => `${it.quantity}x ${it.product_name}`).join(', ')}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {mergeCustomerMismatch && (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800">
              Selected repairs belong to different customers - make sure this is intended.
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Cart link title</label>
            <input
              type="text"
              value={mergeTitle}
              onChange={(e) => {
                setMergeTitle(e.target.value);
                setMergeTitleEdited(true);
              }}
              className="w-full px-3 py-1.5 text-sm border-2 border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <DiscountOptions
            apply={mergeApplyDiscount}
            onApply={setMergeApplyDiscount}
            percent={mergeDiscountPercent}
            onPercent={setMergeDiscountPercent}
            valid={mergeDiscountValid}
          />

          {mergeError && (
            <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">{mergeError}</div>
          )}

          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setMergeOpen(false)}
              disabled={merging}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleMerge}
              disabled={selectedMergeIds.length === 0 || !mergeTitle.trim() || !mergeDiscountValid || merging}
              className="px-3 py-1.5 text-sm font-medium text-white bg-naviter-blue hover:bg-naviter-blue-dark rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
            >
              {merging ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent mr-1.5"></div>
                  Creating cart...
                </>
              ) : (
                <>
                  <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
                  Create combined cart & attach
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Send to customer modal */}
      <Modal
        isOpen={!!sendModalLink}
        onClose={() => !sending && setSendModalLink(null)}
        title="Send payment link via Help Scout"
        size="sm"
      >
        {sendModalLink && (
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-md px-3 py-2 text-sm">
              <div className="font-medium text-gray-900 truncate">{sendModalLink.label || 'Payment link'}</div>
              <div className="text-xs text-gray-500 truncate" title={sendModalLink.url}>{sendModalLink.url}</div>
            </div>

            {sendModalLink.sent_at && (
              <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800">
                Already sent to {sendModalLink.sent_to} on {formatDateTime(sendModalLink.sent_at)} - sending again.
              </div>
            )}

            {hsConversationId ? (
              <div className="bg-gray-50 rounded-md px-3 py-2 text-xs text-gray-600">
                A draft reply (payment email template with this link) will be prepared in Help Scout conversation{' '}
                <a
                  href={helpScoutLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-naviter-blue hover:underline"
                >
                  #{hsConversationId}
                </a>
                . The conversation opens in a new tab - review the draft there and send it.
              </div>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800">
                  This repair has no Help Scout conversation yet - a new one will be started for the customer below,
                  with the payment email prepared as a draft. Nothing is sent until you review it in Help Scout.
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Customer email (starts the new conversation)</label>
                  <input
                    type="email"
                    value={sendEmail}
                    onChange={(e) => setSendEmail(e.target.value)}
                    placeholder="customer@example.com"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            {sendError && (
              <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">{sendError}</div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setSendModalLink(null)}
                disabled={sending}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={!sendReady || sending}
                className="px-3 py-1.5 text-sm font-medium text-white bg-naviter-blue hover:bg-naviter-blue-dark rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent mr-1.5"></div>
                    Creating draft...
                  </>
                ) : (
                  <>
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                    Create draft in Help Scout
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
});

export default PaymentLinks;
